/**
 * Add or edit one line a supplier carries.
 *
 * A line is either a catalogue ingredient (picked from the org's list, which
 * fills the name and bottle size) or something with no recipe role — limes,
 * block ice — typed by name. The API keys the row by the ingredient id when
 * there is one and by the slugified name otherwise; this form derives the same
 * id so a repeat save is the same row.
 */

import { useMemo, useState, type FormEvent } from "react";
import { PACK_UNITS, slugify, type Ingredient, type PackUnit } from "@baros/core";
import type { SupplierItem } from "./api.js";

interface Props {
  readonly initial?: SupplierItem | undefined;
  readonly ingredients: readonly Ingredient[];
  readonly onCancel: () => void;
  readonly onSave: (itemId: string, body: Record<string, unknown>) => Promise<void>;
}

const NONE = "__none__";

export function ItemForm({ initial, ingredients, onCancel, onSave }: Props) {
  const sorted = useMemo(() => [...ingredients].sort((a, b) => a.name.localeCompare(b.name)), [ingredients]);
  const [ingredientId, setIngredientId] = useState<string>(initial?.ingredientId ?? NONE);
  const [name, setName] = useState(initial?.name ?? "");
  const [pack, setPack] = useState(initial?.pack ?? "");
  const [packCost, setPackCost] = useState(initial ? (initial.unitCostCents / 100).toFixed(2) : "");
  const [par, setPar] = useState(String(initial?.par ?? 1));
  const [packSize, setPackSize] = useState(String(initial?.packSize ?? 1));
  const [unit, setUnit] = useState<PackUnit>(initial?.unit ?? "btl");
  const [unitMl, setUnitMl] = useState(initial?.unitMl ? String(initial.unitMl) : "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function pickIngredient(id: string) {
    setIngredientId(id);
    const ing = sorted.find((i) => i.ingredientId === id);
    if (ing) {
      setName(ing.name);
      if (ing.bottleMl) setUnitMl(String(ing.bottleMl));
      if (!pack && ing.bottleMl) setPack(`6 × ${ing.bottleMl} mL`);
      if (!pack && ing.bottleMl) setPackSize("6");
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) return setError("The line needs a name.");
    if (!pack.trim()) return setError("Describe the pack, e.g. 6 × 700 mL.");
    const cents = Math.round(Number(packCost) * 100);
    if (!Number.isFinite(cents) || cents < 0) return setError("Pack cost must be a number of dollars.");
    if (!/^\d+$/.test(packSize) || Number(packSize) < 1) return setError("Pack size must be a whole number of units.");
    if (!Number.isFinite(Number(par)) || Number(par) < 0) return setError("Par must be a number.");
    if (unit === "btl" && unitMl && !(Number(unitMl) > 0)) return setError("Bottle size must be millilitres.");

    const id = initial?.itemId ?? (ingredientId !== NONE ? ingredientId : slugify(name));
    const body: Record<string, unknown> = {
      ingredientId: ingredientId === NONE ? null : ingredientId,
      name: name.trim(),
      pack: pack.trim(),
      unitCostCents: cents,
      par: Number(par),
      packSize: Number(packSize),
      unit,
    };
    if (unit === "btl" && unitMl) body["unitMl"] = Number(unitMl);

    setBusy(true);
    try {
      await onSave(id, body);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save the line");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="sup-form" onSubmit={submit}>
      <div className="label" style={{ gridColumn: "1 / -1" }}>{initial ? `Editing ${initial.name}` : "New line"}</div>
      <label className="field" style={{ gridColumn: "1 / -1" }}>
        <span>Catalogue ingredient</span>
        <select value={ingredientId} onChange={(e) => pickIngredient(e.target.value)} disabled={Boolean(initial)}>
          <option value={NONE}>Not in the recipe catalogue (produce, ice, packaging)</option>
          {sorted.map((i) => (
            <option key={i.ingredientId} value={i.ingredientId}>{i.name}{i.reviewed ? "" : " *"}</option>
          ))}
        </select>
      </label>
      <label className="field" style={{ gridColumn: "1 / -1" }}>
        <span>Name as the supplier lists it</span>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
      </label>
      <label className="field"><span>Pack</span><input type="text" placeholder="6 × 700 mL" value={pack} onChange={(e) => setPack(e.target.value)} required /></label>
      <label className="field"><span>Pack cost, $ ex GST</span><input className="num-input" style={{ width: "100%" }} type="number" min={0} step={0.01} value={packCost} onChange={(e) => setPackCost(e.target.value)} required /></label>
      <label className="field">
        <span>Unit</span>
        <select value={unit} onChange={(e) => setUnit(e.target.value as PackUnit)}>
          {PACK_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
      </label>
      <label className="field"><span>Units per pack</span><input className="num-input" style={{ width: "100%" }} type="number" min={1} step={1} value={packSize} onChange={(e) => setPackSize(e.target.value)} /></label>
      <label className="field"><span>Par, in units</span><input className="num-input" style={{ width: "100%" }} type="number" min={0} step={0.5} value={par} onChange={(e) => setPar(e.target.value)} /></label>
      {unit === "btl" && (
        <label className="field"><span>Bottle size, mL</span><input className="num-input" style={{ width: "100%" }} type="number" min={1} step={1} value={unitMl} onChange={(e) => setUnitMl(e.target.value)} /></label>
      )}
      {error && <div className="msg err" style={{ gridColumn: "1 / -1" }}>{error}</div>}
      <div className="row" style={{ gridColumn: "1 / -1", justifyContent: "flex-end" }}>
        <button type="button" className="btn" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="submit" className="btn primary" style={{ height: 48 }} disabled={busy}>
          {busy ? "Saving…" : initial ? "Save line" : "Add line"}
        </button>
      </div>
    </form>
  );
}
